<template>
  <div class="flex items-center bg-[#F9F9FA] rounded-md px-3 py-2.5 mb-3.5">
    <div class="flex items-center flex-1 min-w-0">
      <div class="size-[44px] shrink-0 mr-2.5">
        <Thumbnail
          :type="fileTypeToThumbnail(report.copyright.type)"
          :src="copyrightImage"
        />
      </div>
      <div class="min-w-0 w-full" v-truncate>
        <div class="text-[12px] leading-[16px] text-gray-500 mb-1">
          Your registered work
        </div>
        <div
          data-truncate-target
          class="font-medium text-[14px] leading-[18px] text-gray-800"
        >
          {{ copyrightName }}
        </div>
      </div>
    </div>

    <SortIcon class="size-[18px] shrink-0 mx-3 rotate-90 text-gray-800" />

    <div class="flex items-center flex-1 min-w-0">
      <div class="size-[44px] shrink-0 mr-2.5">
        <Thumbnail
          :type="fileTypeToThumbnail(report.infringingFile.type)"
          :src="infringingImage"
        />
      </div>
      <div class="min-w-0 w-full" v-truncate>
        <div class="text-[12px] leading-[16px] text-gray-500 mb-1">
          Potential infringing work
        </div>
        <div
          data-truncate-target
          class="font-medium text-[14px] leading-[18px] text-gray-800"
        >
          {{ infringingName }}
        </div>
      </div>
    </div>
  </div>

  <InviteInput
    v-model="usersToInvite"
    size="lg"
    :show-own-entity="true"
    class="mb-3.5"
  />

  <div class="flex justify-end mb-3.5">
    <Button
      variant="primary"
      size="md"
      :disabled="!usersToInvite.length"
      :loading="sendInviteLoading"
      @click="handleSendInvite"
    >
      Send invite
    </Button>
  </div>

  <Table v-if="viewerEdges.length" class="border-t-0">
    <Table.Head>
      <Table.Header primary>Name</Table.Header>
      <Table.Header class="w-[50px] shrink-0" />
    </Table.Head>

    <Table.Row
      v-for="(edge, index) in viewerEdges"
      :key="getViewerKey(edge.node)"
      :hover="false"
      :divider="index < viewerEdges.length - 1"
      class="cursor-default!"
    >
      <template #media>
        <Avatar
          v-if="!isEmailInvite(edge.node)"
          :src="getViewerAvatarUrl(edge.node)"
          :size="32"
        />
        <div
          v-else
          class="flex size-8 items-center justify-center text-gray-800"
        >
          <SendIcon class="size-[22px]" />
        </div>
      </template>

      <Table.Cell primary>
        <div class="min-w-0 flex-1">
          <div class="truncate text-[14px] leading-[18px] text-gray-800">
            {{ getViewerName(edge.node) }}
          </div>
          <div
            v-if="getViewerSecondaryText(edge.node)"
            class="truncate text-[13px] leading-[18px] text-gray-500"
          >
            {{ getViewerSecondaryText(edge.node) }}
          </div>
        </div>
      </Table.Cell>

      <Table.Cell align="right" class="shrink-0 gap-x-2">
        <span
          v-if="isEmailInvite(edge.node)"
          class="text-[14px] leading-5 text-gray-500"
        >
          Invited
        </span>
        <RowActionsMenu @remove="handleRemove(edge.node)" />
      </Table.Cell>
    </Table.Row>
  </Table>

  <div class="bg-[#F9F9FA] rounded-md px-3 pt-[15px] pb-2.5 mt-3.5">
    <div class="flex items-center justify-between">
      <div class="flex-1 min-w-0">
        <div
          class="flex items-center font-medium text-[14px] leading-[18px] text-gray-800"
        >
          <LinkIcon class="mr-2 size-3.5 shrink-0 text-gray-800" />
          <div>Public link</div>
        </div>
        <div class="text-[14px] leading-[18px] text-gray-800 mt-0.5">
          Public links do not contain playable media.
        </div>
      </div>
      <Toggle
        :model-value="publicLinkEnabled"
        :size="isDesktopLayout ? 'md' : 'lg'"
        class="ml-3 shrink-0"
        @update:model-value="handlePublicLinkToggle"
      />
    </div>

    <div
      v-if="publicLinkEnabled"
      class="flex items-center bg-white border border-[#D4D8E1] rounded-md h-[41px] pl-[14px] pr-[6px] mt-2.5"
    >
      <div
        class="flex-1 min-w-0 truncate text-[14px] leading-[18px] text-gray-800"
      >
        {{ publicUrl }}
      </div>
      <Button
        variant="primary"
        size="md"
        class="ml-2 shrink-0"
        @click="handleCopyLink"
      >
        {{ copied ? 'Copied' : 'Copy link' }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useApolloClient, useMutation } from '@vue/apollo-composable'
import { Avatar, Button, Table, Thumbnail, Toggle } from '@dacr/library'
import { LinkIcon, SendIcon, SortIcon } from '@dacr/icons'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { graphql } from '@/shared/graphql/router'
import {
  InviteType,
  type InviteInput as InvitePayload,
  type ReportQuery,
} from '@/shared/graphql/router/graphql'
import type { Reference, StoreObject } from '@apollo/client'
import type { Entity as LegacyEntity } from '@/shared/graphql/legacy/generated'
import InviteInput from '@/features/search/components/InviteInput.vue'
import RowActionsMenu from '@/shared/dialogs/file/RowActionsMenu.vue'
import { useDevice } from '@/shared/composables/useDevice'
import { EMAIL_REGEX } from '@/shared/utils/string'

type ReportViewerEdge = NonNullable<
  NonNullable<NonNullable<ReportQuery['report']>['viewers']>['edges'][number]
>
type ReportViewerNode = ReportViewerEdge['node']
type ShareReport = NonNullable<ReportQuery['report']>

const ADD_REPORT_VIEWERS_MUTATION = graphql(`
  mutation AddReportViewers($input: AddReportViewersInput!) {
    addReportViewers(input: $input) {
      error {
        message
      }
      report {
        id
        settings {
          isPrivate
        }
        viewers(input: { first: 100, filterBy: ALL }) {
          totalCount
          edges {
            cursor
            role
            node {
              ... on User {
                id
                name
                username
                avatar {
                  url
                }
              }
              ... on Organization {
                id
                name
                avatar {
                  url
                }
                owner {
                  ... on User {
                    id
                    name
                  }
                }
              }
              ... on EmailInvite {
                email
              }
            }
          }
        }
      }
    }
  }
`)

const REMOVE_REPORT_VIEWER_MUTATION = graphql(`
  mutation RemoveReportViewer($input: RemoveReportViewerInput!) {
    removeReportViewer(input: $input) {
      success
    }
  }
`)

const UPDATE_REPORT_SETTINGS_MUTATION = graphql(`
  mutation UpdateReportSettings($input: UpdateReportSettingsInput!) {
    updateReportSettings(input: $input) {
      report {
        id
        settings {
          isPrivate
        }
      }
    }
  }
`)

const props = defineProps<{
  report: ShareReport
  getReport?: () => ShareReport | undefined
  publicUrl?: string
}>()

const toastStore = useToastStore()
const { isDesktopLayout } = useDevice()
const { client: apolloClient } = useApolloClient('router')
const report = computed(() => props.getReport?.() ?? props.report)

const usersToInvite = ref<LegacyEntity[]>([])
const publicLinkEnabled = ref(!report.value.settings?.isPrivate)
const copied = ref(false)
const removingViewerKey = ref<string>()
let copiedTimeout: ReturnType<typeof setTimeout> | undefined

const { mutate: addReportViewers, loading: sendInviteLoading } = useMutation(
  ADD_REPORT_VIEWERS_MUTATION,
  { clientId: 'router' }
)

const { mutate: removeReportViewer } = useMutation(
  REMOVE_REPORT_VIEWER_MUTATION,
  { clientId: 'router' }
)

const { mutate: updateReportSettings } = useMutation(
  UPDATE_REPORT_SETTINGS_MUTATION,
  { clientId: 'router' }
)

watch(
  () => report.value.settings?.isPrivate,
  (isPrivate) => {
    if (isPrivate === undefined) return
    publicLinkEnabled.value = !isPrivate
  }
)

const viewerEdges = computed<ReportViewerEdge[]>(
  () => report.value.viewers?.edges ?? []
)

const publicUrl = computed(
  () =>
    props.publicUrl ??
    `${window.location.origin}/infringement/report/${report.value.id}`
)

const getInviteType = (user: LegacyEntity) => {
  if (EMAIL_REGEX.test(user.id)) return InviteType.Email
  return user.__typename === 'BasicUser' ? InviteType.User : InviteType.Org
}

const buildInviteInput = (user: LegacyEntity): InvitePayload => ({
  ...(getInviteType(user) === InviteType.Email
    ? { email: user.id }
    : { id: user.id }),
  type: getInviteType(user),
})

const buildViewerInviteInput = (node: ReportViewerNode): InvitePayload => {
  if (isEmailInvite(node)) {
    return { email: node.email, type: InviteType.Email }
  }

  return {
    id: node.id,
    type: node.__typename === 'Organization' ? InviteType.Org : InviteType.User,
  }
}

const isEmailInvite = (
  item: ReportViewerNode
): item is Extract<ReportViewerNode, { email: string }> => 'email' in item

const getViewerKey = (item: ReportViewerNode) =>
  isEmailInvite(item) ? item.email : item.id

const getViewerName = (item: ReportViewerNode) =>
  isEmailInvite(item) ? item.email : item.name || 'Unknown'

const getViewerAvatarUrl = (item: ReportViewerNode) =>
  isEmailInvite(item) ? undefined : item.avatar.url

const getViewerSecondaryText = (item: ReportViewerNode) => {
  if (isEmailInvite(item)) return ''
  if ('username' in item) return item.username || ''
  return 'owner' in item && item.owner?.__typename === 'User'
    ? item.owner.name
    : ''
}

const getInviteErrorMessage = (message?: string | null) =>
  message || 'Something went wrong - please try again later'

const removeViewerFromCache = (viewer: ReportViewerNode) => {
  const reportCacheId = apolloClient.cache.identify({
    __typename: 'Report',
    id: report.value.id,
  })

  if (!reportCacheId) return

  const viewerKey = getViewerKey(viewer)

  apolloClient.cache.modify({
    id: reportCacheId,
    fields: {
      viewers(existingViewers = {}, { readField }) {
        const existing = existingViewers as {
          totalCount?: number
          edges?: ReadonlyArray<Reference | StoreObject>
        }

        if (!existing.edges) return existingViewers

        return {
          ...existing,
          totalCount:
            typeof existing.totalCount === 'number'
              ? Math.max(existing.totalCount - 1, 0)
              : existing.totalCount,
          edges: existing.edges.filter((edge) => {
            const node = readField<Reference | StoreObject>('node', edge)
            if (!node) return true

            const key =
              readField<string>('email', node) ?? readField<string>('id', node)

            return key !== viewerKey
          }),
        }
      },
    },
  })
}

const copyrightImage = computed(() => {
  const url = report.value.copyright.asset?.previewImageUrl
  return url ? applyAuthorizationToken(url) : undefined
})

const infringingImage = computed(() => {
  const url = report.value.infringingFile.asset?.previewImageUrl
  return url ? applyAuthorizationToken(url) : undefined
})

const copyrightName = computed(
  () =>
    report.value.copyright.metadata?.originalFilename ||
    report.value.copyright.name
)

const infringingName = computed(() => report.value.infringingFile.name)

const handleSendInvite = async () => {
  if (!usersToInvite.value.length) return

  try {
    const response = await addReportViewers({
      input: {
        reportId: report.value.id,
        invites: usersToInvite.value.map(buildInviteInput),
      },
    })

    const error = response?.data?.addReportViewers.error

    if (error) {
      throw new Error(getInviteErrorMessage(error.message))
    }

    usersToInvite.value = []

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Invites were successfully sent',
    })
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content:
        error instanceof Error
          ? error.message
          : 'Something went wrong - please try again later',
      error: error as Error,
    })
  }
}

const handleRemove = async (viewer: ReportViewerNode) => {
  if (removingViewerKey.value) return

  try {
    removingViewerKey.value = getViewerKey(viewer)
    const response = await removeReportViewer({
      input: {
        reportId: report.value.id,
        invite: buildViewerInviteInput(viewer),
      },
    })

    if (response?.data?.removeReportViewer.success) {
      removeViewerFromCache(viewer)
    }
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Something went wrong - please try again later',
      error: error as Error,
    })
  } finally {
    removingViewerKey.value = undefined
  }
}

const handlePublicLinkToggle = async (value: boolean) => {
  const previousValue = publicLinkEnabled.value
  publicLinkEnabled.value = value

  try {
    await updateReportSettings({
      input: {
        id: report.value.id,
        isPrivate: !value,
      },
    })
  } catch (error) {
    publicLinkEnabled.value = previousValue
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Something went wrong - please try again later',
      error: error as Error,
    })
  }
}

const handleCopyLink = async () => {
  if (copiedTimeout) clearTimeout(copiedTimeout)
  await navigator.clipboard.writeText(publicUrl.value)
  copied.value = true
  copiedTimeout = setTimeout(() => {
    copied.value = false
  }, 1500)
}
</script>
